import React, { useState, useEffect, useRef } from 'react';
import { PlayerProfile, HuntrixMember, IdolRank } from '../types';
import { ExitButton } from './ExitButton';
import { MusicManager } from './audio/GameAudioManager';
import * as Tone from 'tone';
import { Hand } from './Hand';
import {
    Music,
    RefreshCw,
    Zap,
    Users,
    Trophy,
    Skull,
    Tv,
    Wine,
    HelpCircle,
    X,
    Shield,
    ArrowDown,
    DollarSign
} from 'lucide-react';
import { Notification } from './NotificationSystem';
import { RANKS } from '../constants';
import { TheSlide } from './TheSlide';

interface VarietyShowProps {
    player: PlayerProfile;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: Notification['type']) => void;
}

// --- TYPES ---
type GamePhase = 'MENU' | 'SETUP' | 'CHANTING' | 'CALLING' | 'STACKING' | 'RESULT';

interface Participant {
    id: string;
    name: string;
    color: string;
    textColor: string;
    isPlayer: boolean;
    angle?: number;
}

// Helper to distribute angles
const assignAngles = (participants: Participant[]) => {
    const step = 360 / participants.length;
    return participants.map((p, i) => ({
        ...p,
        angle: i * step
    }));
};

const GUEST_POOL: Participant[] = [
    { id: 'GUEST_ROSE', name: 'ROSÉ', color: 'bg-rose-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_BRUNO', name: 'BRUNO MARS', color: 'bg-zinc-800', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_JINU', name: 'JINU', color: 'bg-violet-600', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_GWIMA', name: 'GWI-MA', color: 'bg-red-900', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_CELINE', name: 'CELINE', color: 'bg-emerald-600', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_BOBBY', name: 'BOBBY', color: 'bg-blue-600', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_HEALER', name: 'HEALER HAN', color: 'bg-green-700', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_HOST', name: 'HOST LIZA', color: 'bg-yellow-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_ROMANCE', name: 'ROMANCE SAJA', color: 'bg-pink-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_HOSHI', name: 'HOSHI', color: 'bg-orange-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_YEJI', name: 'YEJI', color: 'bg-blue-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_YEONJUN', name: 'YEONJUN', color: 'bg-blue-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_HYUNJIN', name: 'HYUNJIN', color: 'bg-red-600', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_WONYOUNG', name: 'WONYOUNG', color: 'bg-purple-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_SANA', name: 'SANA', color: 'bg-stone-300', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_MINNIE', name: 'MINNIE', color: 'bg-indigo-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_KARINA', name: 'KARINA', color: 'bg-fuchsia-600', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_JACKSON', name: 'JACKSON', color: 'bg-zinc-700', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_NAYEON', name: 'NAYEON', color: 'bg-sky-300', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_JEONGYEON', name: 'JEONGYEON', color: 'bg-emerald-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_JIHYO', name: 'JIHYO', color: 'bg-amber-500', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_CHAEYOUNG', name: 'CHAEYOUNG', color: 'bg-red-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_EJAE', name: 'EJAE', color: 'bg-purple-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_AUDREY', name: 'AUDREY NUNA', color: 'bg-pink-400', textColor: 'text-white', isPlayer: false },
    { id: 'GUEST_REI', name: 'REI AMI', color: 'bg-cyan-400', textColor: 'text-white', isPlayer: false },
];

const SPIRIT_PENALTY = 20;
// BUFF: Increased from 15,000 to 1,000,000 base
const APPEARANCE_FEE = 1000000;
const SHIELD_THRESHOLD = 4.0;

export const VarietyShow: React.FC<VarietyShowProps> = ({ player, onUpdatePlayer, onExit, onNotify }) => {
    const [activeGame, setActiveGame] = useState<'NONE' | 'APATEU' | 'SLIDE'>('NONE');
    const [phase, setPhase] = useState<GamePhase>('MENU');
    const [selectedMemberId, setSelectedMemberId] = useState<string>(player.group.members[0].id);
    const [showRules, setShowRules] = useState(false);

    const [chantCount, setChantCount] = useState(0);
    const [hypeMultiplier, setHypeMultiplier] = useState(1.0);
    const [lastClickTime, setLastClickTime] = useState(0);
    const [rhythmFeedback, setRhythmFeedback] = useState<string>("");
    const [isBeat, setIsBeat] = useState(false);

    const [targetNumber, setTargetNumber] = useState(7);
    const [stack, setStack] = useState<Participant[]>([]);
    const [currentFloor, setCurrentFloor] = useState(0);
    const [resultMessage, setResultMessage] = useState("");
    const [gameParticipants, setGameParticipants] = useState<Participant[]>([]);

    const stackIntervalRef = useRef<number | null>(null);
    const metronomeRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const getSelectedMember = () => player.group.members.find(m => m.id === selectedMemberId)!;

    const getMemberColor = (id: string) => {
        switch (id) {
            case 'RUMI': return 'bg-purple-600';
            case 'MIRA': return 'bg-pink-600';
            case 'ZOEY': return 'bg-cyan-600';
            default: return 'bg-slate-500';
        }
    };

    const startGame = async () => {
        const member = getSelectedMember();
        if (member.spirit < SPIRIT_PENALTY) {
            onNotify(`${member.name} is too exhausted (Low Spirit) to handle another round of drinks!`, 'error');
            return;
        }

        // Attempt to wake up audio on interaction
        await MusicManager.initialize();

        const playerPart: Participant = {
            id: member.id,
            name: member.name.toUpperCase(),
            color: getMemberColor(member.id),
            textColor: 'text-white',
            isPlayer: true
        };

        const guests = [...GUEST_POOL].sort(() => Math.random() - 0.5).slice(0, 3);
        // Assign random angles or distribute evenly? Evenly is better for visual clarity.
        // We need to form the table now.
        const rawTable = [playerPart, ...guests].sort(() => Math.random() - 0.5);

        // Assign angles
        const step = 360 / rawTable.length;
        const table = rawTable.map((p, i) => ({
            ...p,
            angle: i * step
        }));

        setGameParticipants(table);
        setPhase('CHANTING');
        setChantCount(0);
        setHypeMultiplier(1.0);
        setRhythmFeedback("LISTEN TO THE CLAP!");
        setLastClickTime(Date.now());
    };

    const handleChantClick = () => {
        const now = Date.now();
        const diff = now - lastClickTime;
        setLastClickTime(now);

        if (MusicManager.ctx && Tone.context.state === 'running') {
            MusicManager.playSFX("kick", "C2", "32n");
        }

        let quality = 1.0;
        let feedback = "";

        if (chantCount === 0) {
            feedback = "APATEU!";
        } else {
            const error = Math.abs(diff - 500);
            if (error < 100) {
                quality = 0.5;
                feedback = "PERFECT!";
            } else if (error < 250) {
                quality = 0.2;
                feedback = "GOOD!";
            } else {
                quality = 0.0;
                feedback = "OFF BEAT...";
            }
        }

        setHypeMultiplier(prev => Math.min(5.0, prev + quality));
        setRhythmFeedback(feedback);

        if (chantCount >= 3) {
            setPhase('CALLING');
        } else {
            setChantCount(prev => prev + 1);
        }
    };

    const commitNumber = () => {
        setPhase('STACKING');
        // Initialize stack with all participants (shuffled order from setup)
        const roundStack = [...gameParticipants];
        setStack(roundStack);

        setCurrentFloor(0);
        setResultMessage("");

        let currentCount = 0;

        if (stackIntervalRef.current) clearInterval(stackIntervalRef.current);

        stackIntervalRef.current = window.setInterval(() => {
            currentCount++;
            setCurrentFloor(currentCount);

            if (MusicManager.ctx && Tone.context.state === 'running') {
                MusicManager.playSFX("hihat", "32n");
            }

            // Cycle: Remove from bottom, add to top
            const movedHand = roundStack.pop();
            if (movedHand) {
                roundStack.unshift(movedHand);
            }
            setStack([...roundStack]);

            if (currentCount >= targetNumber) {
                if (stackIntervalRef.current) clearInterval(stackIntervalRef.current);
                if (movedHand) finalizeGame(movedHand);
            }
        }, 400);
    };

    const finalizeGame = (loser: Participant) => {
        setPhase('RESULT');
        const isPlayerLoser = loser.isPlayer;

        const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
        const rankMultiplier = rankData?.multiplier || 1;

        if (!isPlayerLoser) {
            // BUFF: Increased base from 2k to 50k
            const fanGain = Math.floor(50000 * hypeMultiplier * rankMultiplier);
            // BUFF: Increased from fee/5 (~3k) to fee/4 (~250k)
            const cashGain = (APPEARANCE_FEE / 4) * rankMultiplier;
            onUpdatePlayer({
                fans: player.fans + fanGain,
                cash: player.cash + cashGain
            });
            setResultMessage(`${loser.name} DRINKS! (while you gained +${fanGain.toLocaleString()} fans)`);
        } else {
            const memberId = loser.id;
            const hasShield = hypeMultiplier >= SHIELD_THRESHOLD;
            const damage = hasShield ? Math.floor(SPIRIT_PENALTY / 2) : SPIRIT_PENALTY;

            const currentMember = player.group.members.find(m => m.id === memberId)!;
            const newSpirit = Math.max(0, currentMember.spirit - damage);
            const pityFans = 5000 * rankMultiplier; // BUFF: Increased from 500

            const updatedMembers = player.group.members.map(m =>
                m.id === memberId ? { ...m, spirit: newSpirit } : m
            );

            onUpdatePlayer({
                group: { ...player.group, members: updatedMembers },
                fans: player.fans + pityFans
            });

            if (hasShield) {
                setResultMessage(`${loser.name} CAUGHT! (SHIELDED: -${damage} SP)`);
            } else {
                setResultMessage(`${loser.name} CAUGHT! (-${damage} SPIRIT)`);
            }
        }
    };

    const resetGame = () => {
        setPhase('SETUP');
        setStack([]);
        setRhythmFeedback("");
    };

    const handleExit = () => {
        const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
        const rankMultiplier = rankData?.multiplier || 1;
        const finalFee = APPEARANCE_FEE * rankMultiplier;

        onUpdatePlayer({ cash: player.cash + finalFee });
        onNotify(`Show wrapped! Received Appearance Fee: $${finalFee.toLocaleString()}`, 'success');
        onExit();
    };

    useEffect(() => {
        if (phase === 'CHANTING') {
            const interval = setInterval(() => {
                if (MusicManager.ctx && Tone.context.state === 'running') {
                    MusicManager.playSFX("clap", "16n");
                }
                setIsBeat(true);
                setTimeout(() => setIsBeat(false), 100);
            }, 500);
            metronomeRef.current = interval;
        } else {
            if (metronomeRef.current) clearInterval(metronomeRef.current);
        }
        return () => {
            if (metronomeRef.current) clearInterval(metronomeRef.current);
        };
    }, [phase]);

    useEffect(() => {
        return () => {
            if (stackIntervalRef.current) clearInterval(stackIntervalRef.current);
            if (metronomeRef.current) clearInterval(metronomeRef.current);
        };
    }, []);

    if (activeGame === 'SLIDE') {
        return <TheSlide player={player} onUpdatePlayer={onUpdatePlayer} onExit={() => setActiveGame('NONE')} onNotify={onNotify} />;
    }

    // --- MAIN MENU SELECTOR ---
    if (activeGame === 'NONE') {
        return (
            <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-8 relative">
                <ExitButton onExit={onExit} />
                <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-8">
                    <button
                        onClick={() => { setActiveGame('APATEU'); setPhase('SETUP'); }}
                        className="group relative h-96 bg-slate-900 border-2 border-pink-500 rounded-2xl overflow-hidden hover:scale-105 transition-transform"
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10"></div>
                        <div className="absolute bottom-0 left-0 p-8 z-20 text-left">
                            <h2 className="text-4xl font-heading text-white mb-2 group-hover:text-pink-400">APATEU</h2>
                            <p className="text-slate-300">The "Apartment" drinking game. Rhythm & Luck.</p>
                        </div>
                        <div className="absolute top-4 right-4 z-20 bg-pink-600 text-white px-3 py-1 rounded font-bold text-xs">POPULAR</div>
                        <Tv size={120} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-800 group-hover:text-pink-900 transition-colors" />
                    </button>

                    <button
                        onClick={() => setActiveGame('SLIDE')}
                        className="group relative h-96 bg-slate-900 border-2 border-cyan-500 rounded-2xl overflow-hidden hover:scale-105 transition-transform"
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-10"></div>
                        <div className="absolute bottom-0 left-0 p-8 z-20 text-left">
                            <h2 className="text-4xl font-heading text-white mb-2 group-hover:text-cyan-400">THE SLIDE</h2>
                            <p className="text-slate-300">Dodging obstacles in squeaky leather suits.</p>
                        </div>
                        <ArrowDown size={120} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-800 group-hover:text-cyan-900 transition-colors" />
                    </button>
                </div>
            </div>
        );
    }

    // --- APATEU GAME RENDER ---
    const selectedMember = getSelectedMember();

    return (
        <div className="h-full w-full bg-slate-950 flex flex-col items-center p-2 sm:p-4 relative overflow-y-auto">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/20 via-slate-950 to-slate-950"></div>
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #6366f1 0, #6366f1 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }}></div>

            <ExitButton onExit={() => setActiveGame('NONE')} label="BACK" />

            {showRules && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-indigo-500 rounded-2xl p-6 max-w-md shadow-2xl relative">
                        <button onClick={() => setShowRules(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X /></button>
                        <h3 className="text-2xl font-heading text-indigo-400 mb-4">How to Play: APT.</h3>
                        <div className="space-y-4 text-slate-300 text-sm">
                            <p><strong className="text-white">1. The Chant:</strong> LISTEN to the beat (Clap sound). Click "APT." exactly on the beat to build Hype. <span className="text-pink-400">(Audio Required!)</span></p>
                            <p><strong className="text-white">2. The Call:</strong> Choose a target number (The Floor).</p>
                            <p><strong className="text-white">3. The Stack:</strong> Everyone stacks their hands. Hands are moved from the bottom of the stack to the top, counting up by 1.</p>
                            <p><strong className="text-white">4. The Result:</strong> Whoever's hand lands on top when the count reaches the Target Number <strong className="text-red-400">LOSES</strong> and must drink (Lose Spirit).</p>
                            <p className="italic text-indigo-300">"If your hand is on Floor 7, and I call 7... you drink!"</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="relative z-10 w-full max-w-5xl glass-panel p-4 md:p-8 rounded-3xl shadow-2xl flex flex-col items-center border border-slate-800 min-h-fit mb-24">

                <div className="w-full flex justify-between items-center mb-4 border-b border-slate-800 pb-4 shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-600 rounded-lg shadow-lg">
                            <Tv size={28} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl sm:text-4xl font-heading text-white italic tracking-tighter">THE VARIETY HOUR</h2>
                            <div className="text-indigo-400 text-xs font-bold tracking-widest mt-1">GAME: APATEU (APT.)</div>
                        </div>
                    </div>
                    <button onClick={() => setShowRules(true)} className="p-2 text-indigo-400 hover:text-white transition-colors flex flex-col items-center gap-1">
                        <HelpCircle size={24} />
                        <span className="text-[10px] font-bold">RULES</span>
                    </button>
                </div>

                <div className="flex-1 w-full flex flex-col items-center justify-start relative pt-6">

                    {phase === 'SETUP' && (
                        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-300 w-full max-w-2xl overflow-y-auto pr-2">
                            <div className="text-center space-y-2">
                                <h3 className="text-2xl font-heading text-slate-200">CHOOSE YOUR PLAYER</h3>
                                <p className="text-slate-400 text-sm">Select a member to join the table. Avoid the penalty drink!</p>
                            </div>

                            <div className="grid grid-cols-3 gap-3 w-full">
                                {player.group.members.map(member => (
                                    <button
                                        key={member.id}
                                        onClick={() => setSelectedMemberId(member.id)}
                                        className={`relative p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${selectedMemberId === member.id
                                            ? `bg-opacity-20 border-opacity-100 shadow-[0_0_20px_rgba(255,255,255,0.3)] ${getMemberColor(member.id).replace('bg-', 'border-')}`
                                            : 'bg-slate-900/50 border-slate-700 hover:border-slate-500'
                                            } ${selectedMemberId === member.id ? getMemberColor(member.id).replace('bg-', 'bg-opacity-20 bg-') : ''}`}
                                    >
                                        <div className="font-bold text-base text-white">{member.name}</div>
                                        <div className={`text-[10px] font-bold px-2 py-0.5 rounded ${member.spirit < SPIRIT_PENALTY ? 'bg-red-900/50 text-red-400' : 'bg-slate-800 text-slate-300'}`}>
                                            SP: {member.spirit}/{member.maxSpirit}
                                        </div>
                                        {member.spirit < SPIRIT_PENALTY && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl backdrop-blur-[1px]">
                                                <span className="text-red-500 font-black rotate-12 border-2 border-red-500 px-2 py-1 text-xs">TOO DRUNK</span>
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="flex items-center justify-center gap-8 bg-slate-900/80 p-4 rounded-2xl border border-slate-700 w-full">
                                <div className="text-center">
                                    <div className="text-slate-500 text-[10px] uppercase font-bold mb-1">Win Reward</div>
                                    <div className="text-xl text-green-400 font-mono flex items-center justify-center gap-1">
                                        <Users size={16} /> +Fans
                                    </div>
                                </div>
                                <div className="w-px h-8 bg-slate-700"></div>
                                <div className="text-center">
                                    <div className="text-slate-500 text-[10px] uppercase font-bold mb-1">Penalty</div>
                                    <div className="text-xl text-red-400 font-mono flex items-center justify-center gap-1">
                                        <Wine size={16} /> -{SPIRIT_PENALTY} SP
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={startGame}
                                disabled={selectedMember.spirit < SPIRIT_PENALTY}
                                className="px-12 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-heading text-xl rounded-xl shadow-[0_0_30px_rgba(99,102,241,0.5)] transition-all transform hover:scale-105"
                            >
                                START FILMING
                            </button>
                        </div>
                    )}

                    {phase === 'CHANTING' && (
                        <div className="flex flex-col items-center gap-6 mt-4">
                            <div className="text-2xl md:text-4xl font-black italic text-white animate-bounce text-center h-12 md:h-16 flex items-center justify-center drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                                {rhythmFeedback}
                            </div>

                            <div className="relative">
                                <div className={`absolute inset-0 bg-indigo-500 rounded-full transition-transform duration-100 ${isBeat ? 'scale-125 opacity-40' : 'scale-100 opacity-10'}`}></div>
                                <button
                                    onMouseDown={handleChantClick}
                                    className="w-40 h-40 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-indigo-500 to-pink-600 flex flex-col items-center justify-center shadow-[0_0_50px_rgba(99,102,241,0.6)] hover:scale-105 active:scale-95 transition-all cursor-pointer relative z-10 border-4 border-white/20"
                                >
                                    <span className="text-4xl font-heading text-white tracking-widest pointer-events-none">
                                        APT.
                                    </span>
                                    <span className="text-xs text-indigo-200 mt-2 font-bold tracking-widest">CLICK ON CLAP</span>
                                </button>
                            </div>

                            <div className="flex flex-col items-center gap-2">
                                <div className="flex items-center gap-2 text-sm font-bold text-slate-400">
                                    <span>HYPE LEVEL</span>
                                    <span className="text-yellow-400 text-lg">{hypeMultiplier.toFixed(1)}x REWARDS</span>
                                </div>
                                {hypeMultiplier >= SHIELD_THRESHOLD && (
                                    <div className="flex items-center gap-2 text-xs font-black text-cyan-400 animate-pulse bg-cyan-900/30 px-3 py-1 rounded-full border border-cyan-500/50">
                                        <Shield size={12} />
                                        RHYTHM SHIELD ACTIVE (-50% DMG)
                                    </div>
                                )}
                                <div className="flex gap-2 mt-2">
                                    {[0, 1, 2, 3].map(i => (
                                        <div key={i} className={`w-3 h-3 rounded-full transition-colors ${i < chantCount ? 'bg-pink-500 shadow-[0_0_10px_#ec4899]' : 'bg-slate-800'}`}></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {phase === 'CALLING' && (
                        <div className="flex flex-col items-center gap-8 animate-in fade-in zoom-in duration-300">
                            <div className="text-center">
                                <h3 className="text-2xl font-heading text-white mb-2">CALL THE TARGET FLOOR</h3>
                                <p className="text-slate-400 text-sm">Choose a number between 1 and 20.</p>
                            </div>

                            <div className="flex items-center gap-6 bg-slate-900/50 p-6 rounded-2xl border border-slate-700">
                                <input
                                    type="range"
                                    min="1"
                                    max="20"
                                    value={targetNumber}
                                    onChange={(e) => setTargetNumber(parseInt(e.target.value))}
                                    className="w-64 accent-indigo-500 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="w-20 h-20 bg-indigo-600 rounded-xl flex items-center justify-center text-4xl font-black text-white shadow-lg border-2 border-indigo-400">
                                    {targetNumber}
                                </div>
                            </div>

                            <button
                                onClick={commitNumber}
                                className="px-10 py-4 bg-pink-600 hover:bg-pink-500 text-white font-heading text-xl rounded-xl shadow-[0_0_30px_rgba(236,72,153,0.5)] transition-all transform hover:scale-105"
                            >
                                GAME START!
                            </button>
                        </div>
                    )}

                    {(phase === 'STACKING' || phase === 'RESULT') && (
                        <div className="w-full flex items-center justify-between h-full relative px-4">

                            {/* LEFT PANEL: Current Floor */}
                            <div className="w-1/4 flex flex-col items-center justify-center gap-2">
                                <div className="text-xs text-slate-500 uppercase font-bold tracking-widest text-center">Current Floor</div>
                                <div className="text-6xl md:text-8xl font-black text-white font-mono bg-slate-900/80 px-4 py-4 rounded-2xl border border-slate-700 shadow-xl">
                                    {currentFloor}
                                </div>
                            </div>

                            {/* CENTER PANEL: Game Area */}
                            <div className="flex-1 flex flex-col items-center justify-center relative">

                                {/* Target Floor Indicator (Floating Top Center or nearby) */}
                                <div className="absolute -top-12 bg-slate-900/80 px-4 py-2 rounded-full border border-slate-700 mb-8 z-20">
                                    <span className="text-xs text-slate-400 mr-2 font-bold">TARGET</span>
                                    <span className="text-2xl font-black text-pink-500">{targetNumber}</span>
                                </div>

                                {phase === 'RESULT' && (
                                    <div className={`absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 rounded-2xl`}>
                                        <div className={`px-8 py-6 rounded-3xl border-4 ${resultMessage.includes("DRINKS") ? 'bg-green-600 border-green-400' : 'bg-red-600 border-red-400'} shadow-[0_0_50px_rgba(0,0,0,0.5)] max-w-xl text-center transform scale-110`}>
                                            <div className="text-2xl font-black text-white uppercase italic tracking-tighter flex flex-col items-center gap-2">
                                                {resultMessage.includes("DRINKS") ? <Trophy size={48} className="animate-bounce" /> : <Wine size={48} className="animate-pulse" />}
                                                {resultMessage}
                                            </div>
                                        </div>
                                        <button onClick={resetGame} className="mt-8 px-8 py-3 bg-white text-black hover:bg-slate-200 rounded-full font-bold flex items-center gap-2 shadow-xl hover:scale-105 transition-transform">
                                            <RefreshCw size={20} /> PLAY AGAIN
                                        </button>
                                    </div>
                                )}

                                {/* HANDS PILE CONTAINER */}
                                <div className="relative w-[160px] h-[160px] md:w-[220px] md:h-[220px] flex items-center justify-center">
                                    <div className="relative z-10 w-full h-full flex items-center justify-center">
                                        {stack.map((p, i) => (
                                            <Hand
                                                key={p.id}
                                                index={i}
                                                total={stack.length}
                                                angle={p.angle || 0}
                                                color={p.color}
                                                label={p.name}
                                                subLabel={i === 0 && currentFloor > 0 ? `FL ${currentFloor}` : undefined}
                                                isMoving={i === 0 && currentFloor > 0}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT PANEL: Legend (Players) */}
                            <div className="w-1/4 flex flex-col gap-2 h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                <div className="text-xs text-slate-500 uppercase font-bold tracking-widest text-center mb-2">Players</div>
                                {gameParticipants.map(p => (
                                    <div key={p.id} className="flex items-center gap-2 bg-slate-900/60 p-2 rounded-lg border border-slate-800/50">
                                        <div className={`w-3 h-3 rounded-full shrink-0 ${p.color} shadow-[0_0_5px_currentColor]`}></div>
                                        <span className="text-[10px] font-bold text-slate-300 truncate">{p.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};
